import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, forkJoin, from } from 'rxjs';
import { catchError, map, mergeMap, tap, switchMap, concatMap, toArray, take, withLatestFrom, filter } from 'rxjs/operators';
import * as ServiceRequestActions from './service-request.actions';
import * as ServiceRequestSelectors from './service-request.selectors';
import * as JobCardActions from '../job-card/job-card.actions';
import { Store, Action } from '@ngrx/store';
import { ServiceRequestService } from '@core/services/gse/service-request.service';
import { ChecklistAnswerDto } from 'src/app/api/gse/models';
import { ToastActions } from 'src/app/store/toast/toast.actions';

@Injectable()
export class ServiceRequestEffects {
  private actions$ = inject(Actions);
  private serviceRequestService = inject(ServiceRequestService);
  private store = inject(Store);

  loadServiceRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadServiceRequests),
      switchMap(({ params, preserveSelection }) =>
        this.serviceRequestService.getAllServiceRequests(params).pipe(
          map((response) =>
            ServiceRequestActions.loadServiceRequestsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || (response.data?.length || 0),
              preserveSelection: !!preserveSelection,
            })
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.loadServiceRequestsFailure({
                error: error?.error?.message || 'Failed to load service requests',
              })
            )
          )
        )
      )
    )
  );

  createServiceRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.createServiceRequest),
      mergeMap(({ params, checklistRequest }) =>
        this.serviceRequestService.createServiceRequest(params).pipe(
          switchMap((response) => {
            const newId = response.data?.[0]?.id;
            if (newId && checklistRequest) {
              return this.serviceRequestService.createChecklistAnswer({ serviceRequestId: newId, body: checklistRequest }).pipe(
                map(() => ServiceRequestActions.createServiceRequestSuccess({ response })),
                catchError((error) =>
                  of(
                    ServiceRequestActions.createServiceRequestFailure({
                      error: error?.error?.message || error?.message || 'Service request created but checklist submission failed',
                    })
                  )
                )
              );
            }
            return of(ServiceRequestActions.createServiceRequestSuccess({ response }));
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.createServiceRequestFailure({
                error: error?.error?.message || 'Failed to create service request',
              })
            )
          )
        )
      )
    )
  );

  loadEquipmentOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadEquipmentOptions),
      switchMap(({ params, append }) =>
        this.serviceRequestService.getAllEquipment(params).pipe(
          map((response) =>
            ServiceRequestActions.loadEquipmentOptionsSuccess({
              data: response.data || [],
              totalElements: response.page?.totalElements || (response.data?.length || 0),
              page: response.page?.page || 0,
              last: !!response.page?.last,
              append,
            })
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.loadEquipmentOptionsFailure({
                error: error?.error?.message || 'Failed to load equipment options',
              })
            )
          )
        )
      )
    )
  );

  loadChecklists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadChecklists),
      switchMap(({ params }) =>
        this.serviceRequestService.getChecklists(params).pipe(
          map((response) => {
            // Support both { data: ChecklistDto[] } and raw array response
            const list = Array.isArray(response)
              ? response
              : (response?.data ?? []);
            const checklists = Array.isArray(list) ? list : [];
            return ServiceRequestActions.loadChecklistsSuccess({
              checklists,
            });
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.loadChecklistsFailure({
                error: error?.error?.message || 'Failed to load checklists',
              })
            )
          )
        )
      )
    )
  );

  loadJobCardChecklistQuestions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadJobCardChecklistQuestions),
      switchMap(({ params }) =>
        this.serviceRequestService.getChecklists(params).pipe(
          map((response) => {
            const list = Array.isArray(response)
              ? response
              : (response?.data ?? []);
            const checklists = Array.isArray(list) ? list : [];
            return ServiceRequestActions.loadJobCardChecklistQuestionsSuccess({
              checklists,
            });
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.loadJobCardChecklistQuestionsFailure({
                error: error?.error?.message || 'Failed to load checklist questions',
              })
            )
          )
        )
      )
    )
  );

  submitCheckIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.submitCheckIn),
      mergeMap(({ serviceRequestId, checklistRequest }) =>
        this.serviceRequestService.createChecklistAnswer({ serviceRequestId, body: checklistRequest }).pipe(
          map((response) => {
            const desc = (checklistRequest as { description?: string }).description;
            return ServiceRequestActions.submitCheckInSuccess({
              message: response?.message || 'Checklist submitted successfully',
              response,
              clientDescriptionPatch:
                desc !== undefined
                  ? { serviceRequestId, description: desc }
                  : undefined,
            });
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.submitCheckInFailure({
                error: error?.error?.message || error?.message || 'Failed to submit checklist',
              })
            )
          )
        )
      )
    )
  );

  updateCheckIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.updateCheckIn),
      mergeMap(({ params }) =>
        this.serviceRequestService.updateChecklistAnswer(params).pipe(
          map((response) => {
            const desc = params.body.description;
            return ServiceRequestActions.updateCheckInSuccess({
              message: response?.message || 'Checklist updated successfully',
              response,
              clientDescriptionPatch:
                desc !== undefined
                  ? { serviceRequestId: params.serviceRequestId, description: desc }
                  : undefined,
            });
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.updateCheckInFailure({
                error: error?.error?.message || error?.message || 'Failed to update checklist',
              })
            )
          )
        )
      )
    )
  );

  createJobCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.createJobCard),
      mergeMap(({ params }) =>
        this.serviceRequestService.createJobCard(params).pipe(
          map((response) =>
            ServiceRequestActions.createJobCardSuccess({
              response,
            })
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.createJobCardFailure({
                error: error?.error?.message || 'Failed to create job card',
              })
            )
          )
        )
      )
    )
  );

  updateJobCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.updateJobCard),
      mergeMap(({ params }) =>
        this.serviceRequestService.updateJobCard(params).pipe(
          switchMap((response) => [
            ServiceRequestActions.updateJobCardSuccess({ response }),
            ServiceRequestActions.loadJobCardChecklistAnswers({ params: { id: params.id }, silent: true }),
          ]),
          catchError((error) =>
            of(
              ServiceRequestActions.updateJobCardFailure({
                error: error?.error?.message || 'Failed to update job card',
              })
            )
          )
        )
      )
    )
  );

  createJobCardChecklist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.createJobCardChecklist),
      mergeMap(({ params }) =>
        this.serviceRequestService.createJobCardChecklist(params).pipe(
          switchMap((response) => [
            ServiceRequestActions.createJobCardChecklistSuccess({ response }),
            ServiceRequestActions.loadJobCardChecklistAnswers({ params: { id: params.id } }),
          ]),
          catchError((error) =>
            of(
              ServiceRequestActions.createJobCardChecklistFailure({
                error: error?.error?.message || 'Failed to create job card checklist',
              })
            )
          )
        )
      )
    )
  );

  updateJobCardChecklist$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.updateJobCardChecklist),
      mergeMap(({ params }) =>
        this.serviceRequestService.updateJobCardChecklist(params).pipe(
          switchMap((response) => [
            ServiceRequestActions.updateJobCardChecklistSuccess({ response }),
            ServiceRequestActions.loadJobCardChecklistAnswers({ params: { id: params.id } }),
          ]),
          catchError((error) =>
            of(
              ServiceRequestActions.updateJobCardChecklistFailure({
                error: error?.error?.message || 'Failed to update job card checklist',
              })
            )
          )
        )
      )
    )
  );

  startJobServiceWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.startJobServiceWorkLog),
      mergeMap(({ params, skipReloadAfterSuccess }) =>
        this.serviceRequestService.startJobServiceWorkLog(params).pipe(
          map((response) => ServiceRequestActions.startJobServiceWorkLogSuccess({ response, params, skipReloadAfterSuccess })),
          catchError((error) =>
            of(
              ServiceRequestActions.startJobServiceWorkLogFailure({
                error: error?.error?.message || 'Failed to start job service work log',
                params,
                skipReloadAfterSuccess,
              })
            )
          )
        )
      )
    )
  );

  stopJobServiceWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.stopJobServiceWorkLog),
      mergeMap(({ params, skipReloadAfterSuccess }) =>
        this.serviceRequestService.stopJobServiceWorkLog(params).pipe(
          map((response) => ServiceRequestActions.stopJobServiceWorkLogSuccess({ response, params, skipReloadAfterSuccess })),
          catchError((error) =>
            of(
              ServiceRequestActions.stopJobServiceWorkLogFailure({
                error: error?.error?.message || 'Failed to pause job service work log',
                params,
                skipReloadAfterSuccess,
              })
            )
          )
        )
      )
    )
  );

  finishJobServiceWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.finishJobServiceWorkLog),
      mergeMap(({ params, skipReloadAfterSuccess }) =>
        this.serviceRequestService.finishJobServiceWorkLog(params).pipe(
          map((response) => ServiceRequestActions.finishJobServiceWorkLogSuccess({ response, params, skipReloadAfterSuccess })),
          catchError((error) =>
            of(
              ServiceRequestActions.finishJobServiceWorkLogFailure({
                error: error?.error?.message || 'Failed to finish job service work log',
                params,
                skipReloadAfterSuccess,
              })
            )
          )
        )
      )
    )
  );

  loadJobCardWorkLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadJobCardWorkLogs),
      switchMap(({ params }) =>
        this.serviceRequestService.getJobCardWorkLogs(params).pipe(
          map((response) =>
            ServiceRequestActions.loadJobCardWorkLogsSuccess({
              logs: response.data || [],
            })
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.loadJobCardWorkLogsFailure({
                error: error?.error?.message || 'Failed to load job card work logs',
              })
            )
          )
        )
      )
    )
  );

  updateJobCardStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.updateJobCardStatus),
      mergeMap(({ params }) =>
        this.serviceRequestService.updateJobCardStatus(params).pipe(
          switchMap((response) =>
            this.store.select(ServiceRequestSelectors.selectLastJobCardParams).pipe(
              take(1),
              concatMap((lastParams) => {
                const actions: Action[] = [
                  ServiceRequestActions.updateJobCardStatusSuccess({
                    response,
                    params,
                  }),
                ];
                if (lastParams) {
                  actions.push(
                    ServiceRequestActions.loadJobCards({
                      params: lastParams,
                      preserveSelection: true,
                    })
                  );
                }
                actions.push(JobCardActions.loadJobCardById({ params: { id: params.id } }));
                return actions;
              })
            )
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.updateJobCardStatusFailure({
                error: error?.error?.message || 'Failed to update job card status',
              })
            )
          )
        )
      )
    )
  );

  reloadJobCardsAfterSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ServiceRequestActions.createJobCardSuccess,
        ServiceRequestActions.updateJobCardSuccess,
        ServiceRequestActions.updateJobCardStatusSuccess
      ),
      withLatestFrom(this.store.select(ServiceRequestSelectors.selectLastJobCardParams)),
      map(([, lastParams]) =>
        ServiceRequestActions.loadJobCards({
          params:
            lastParams ?? {
              page: 0,
              size: 10,
            },
          preserveSelection: true,
          silent: true,
        })
      )
    )
  );

  reloadServiceRequestsAfterSubmit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ServiceRequestActions.submitCheckInSuccess,
        ServiceRequestActions.updateCheckInSuccess,
        ServiceRequestActions.createServiceRequestSuccess,
        ServiceRequestActions.createJobCardSuccess
      ),
      withLatestFrom(this.store.select(ServiceRequestSelectors.selectLastServiceRequestParams)),
      map(([action, lastParams]) => {
        // After creating a new check-in, always go back to page 0 so the new item is visible
        const isCreate = action.type === ServiceRequestActions.submitCheckInSuccess.type
          || action.type === ServiceRequestActions.createServiceRequestSuccess.type;
        const baseParams = lastParams ?? { page: 0, size: 10 };
        const params = isCreate ? { ...baseParams, page: 0 } : baseParams;
        return ServiceRequestActions.loadServiceRequests({
          params,
          preserveSelection: true,
          silent: true,
        });
      })
    )
  );

  loadJobCardChecklistAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadJobCardChecklistAnswers),
      switchMap(({ params }) =>
        this.serviceRequestService.getJobCardChecklistAnswers(params).pipe(
          map((response) => {
            const records = response?.data && Array.isArray(response.data)
              ? response.data
              : [];
            const allAnswers = records.flatMap((dto: ChecklistAnswerDto) => dto.answers || []);
            return ServiceRequestActions.loadJobCardChecklistAnswersSuccess({
              records,
              answers: allAnswers,
            });
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.loadJobCardChecklistAnswersFailure({
                error: error?.error?.message || 'Failed to load job card checklist answers',
              })
            )
          )
        )
      )
    )
  );

  loadJobCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.loadJobCards),
      switchMap(({ params, preserveSelection }) =>
        this.serviceRequestService.getAllJobCards(params).pipe(
          map((response) =>
            ServiceRequestActions.loadJobCardsSuccess({
              data: response.data || [],
              totalRecords: response.page?.totalElements || (response.data?.length || 0),
              preserveSelection: !!preserveSelection,
            })
          ),
          catchError((error) =>
            of(
              ServiceRequestActions.loadJobCardsFailure({
                error: error?.error?.message || 'Failed to load job cards',
              })
            )
          )
        )
      )
    )
  );

  recreateJobCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ServiceRequestActions.recreateJobCard),
      withLatestFrom(
        this.store.select(ServiceRequestSelectors.selectJobCards),
        this.store.select(ServiceRequestSelectors.selectLastJobCardParams)
      ),
      mergeMap(([{ params }, jobCards, lastParams]) => {
        // Find the parent job card to copy the inspection date
        const parentJobCard = jobCards.find(jc => jc.id === params.id);

        return this.serviceRequestService.recreateJobCard(params).pipe(
          switchMap((response) => {
            const actions: Action[] = [
              ServiceRequestActions.recreateJobCardSuccess({ response }),
            ];

            // Get the newly created job card ID
            const newJobCardId = response.data?.[0]?.id;
            const parentInspectionDate = parentJobCard?.inspectionDate;
            const parentSupervisor = parentJobCard?.supervisor;
            const parentDescription = parentJobCard?.description ?? '';
            if (newJobCardId && parentInspectionDate && parentSupervisor != null) {
              const parentServices = (parentJobCard?.services ?? []).map((s) => ({
                description: s.description ?? '',
                ...(s.startTime ? { startTime: s.startTime } : {}),
                ...(s.endTime ? { endTime: s.endTime } : {}),
              }));

              actions.push(
                ServiceRequestActions.updateJobCard({
                  params: {
                    id: newJobCardId,
                    body: {
                      description: parentDescription,
                      inspectionDate: parentInspectionDate,
                      supervisorId: parentSupervisor,
                      services: parentServices,
                    }
                  }
                })
              );
            }

            if (lastParams) {
              actions.push(ServiceRequestActions.loadJobCards({ params: lastParams, preserveSelection: true }));
            }

            return actions;
          }),
          catchError((error) =>
            of(
              ServiceRequestActions.recreateJobCardFailure({
                error: error?.error?.message || 'Failed to recreate job card',
              })
            )
          )
        );
      })
    )
  );

  // Success Toasts
  onSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ServiceRequestActions.createServiceRequestSuccess,
        ServiceRequestActions.submitCheckInSuccess,
        ServiceRequestActions.updateCheckInSuccess,
        ServiceRequestActions.createJobCardSuccess,
        ServiceRequestActions.updateJobCardSuccess,
        ServiceRequestActions.startJobServiceWorkLogSuccess,
        ServiceRequestActions.stopJobServiceWorkLogSuccess,
        ServiceRequestActions.finishJobServiceWorkLogSuccess,
        ServiceRequestActions.updateJobCardStatusSuccess,
        ServiceRequestActions.createJobCardChecklistSuccess,
        ServiceRequestActions.updateJobCardChecklistSuccess,
        ServiceRequestActions.recreateJobCardSuccess
      ),
      map((action) => {
        let message = 'Operation successful';
        if ('message' in action && action.message) {
          message = action.message;
        } else if ('response' in action && action.response && action.response.message) {
          message = action.response.message;
        }
        return ToastActions.showSuccessToast({
          summary: 'Success',
          detail: message,
        });
      })
    )
  );

  onFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ServiceRequestActions.createServiceRequestFailure,
        ServiceRequestActions.submitCheckInFailure,
        ServiceRequestActions.updateCheckInFailure,
        ServiceRequestActions.createJobCardFailure,
        ServiceRequestActions.updateJobCardFailure,
        ServiceRequestActions.loadServiceRequestsFailure,
        ServiceRequestActions.loadChecklistsFailure,
        ServiceRequestActions.loadJobCardChecklistQuestionsFailure,
        ServiceRequestActions.loadEquipmentOptionsFailure,
        ServiceRequestActions.loadJobCardsFailure,
        ServiceRequestActions.loadJobCardChecklistAnswersFailure,
        ServiceRequestActions.startJobServiceWorkLogFailure,
        ServiceRequestActions.stopJobServiceWorkLogFailure,
        ServiceRequestActions.finishJobServiceWorkLogFailure,
        ServiceRequestActions.loadJobCardWorkLogsFailure,
        ServiceRequestActions.updateJobCardStatusFailure,
        ServiceRequestActions.createJobCardChecklistFailure,
        ServiceRequestActions.updateJobCardChecklistFailure,
        ServiceRequestActions.recreateJobCardFailure
      ),
      map(({ error }) =>
        ToastActions.showErrorToast({
          summary: 'Error',
          detail: error || 'Operation failed',
        })
      )
    )
  );

  /** After any worklog action (start/pause/finish), reload the job card list and detail
   *  so the job card status (e.g. SCHEDULED → UNDER_PROCESS) is reflected immediately. */
  reloadAfterWorkLog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ServiceRequestActions.startJobServiceWorkLogSuccess,
        ServiceRequestActions.stopJobServiceWorkLogSuccess,
        ServiceRequestActions.finishJobServiceWorkLogSuccess,
      ),
      filter((action) => action.skipReloadAfterSuccess !== true),
      withLatestFrom(this.store.select(ServiceRequestSelectors.selectLastJobCardParams)),
      switchMap(([action, lastParams]) => {
        const acts: Action[] = [];
        if (lastParams) {
          acts.push(ServiceRequestActions.loadJobCards({ params: lastParams, preserveSelection: true, silent: true }));
        }
        acts.push(JobCardActions.loadJobCardById({ params: { id: action.params.jobCardId } }));
        return acts;
      })
    )
  );
}
